import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, CardHeader, DatePicker, Input, MoneyInput, PageHeader, Select, Textarea, useToast } from '../../../components/ui';
import { ItemPicker } from '../../../components/common/Pickers/Pickers';
import { FormSection, KeyValues, TwoColumn } from '../../../components/common/Parts/Parts';
import { serverFieldErrors, trimOrUndef } from '../../../utils/validation';
import { useForm } from '../../../hooks/useForm';
import { money } from '../../../utils/currency';
import { toISODate } from '../../../utils/date';
import { itemService } from '../../materials/services/materialService';
import { stockService } from '../services/stockService';
import type { StockReason } from '../types/stockTypes';
import type { FormEvent } from 'react';
import type { FormErrors } from '../../../hooks/useForm';
import type { Item } from '../../materials/types/materialTypes';

const REASONS: { value: StockReason; label: string; description: string }[] = [
  { value: 'adjustment', label: 'Physical count correction', description: 'Counted stock differs from the app' },
  { value: 'damaged', label: 'Damaged / lost', description: 'Always takes stock out' },
  { value: 'opening', label: 'Opening stock', description: 'Stock you had before using the app' },
];
const DIRECTIONS: { value: 'in' | 'out'; label: string }[] = [
  { value: 'in', label: 'Add to stock' },
  { value: 'out', label: 'Remove from stock' },
];

export default function StockAdjust() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [item, setItem] = useState<Item | null>(null);
  const [saving, setSaving] = useState(false);
  const { form, set, setForm, errors, setErrors } = useForm({
    itemId: params.get('itemId') || null, reason: 'adjustment' as StockReason, direction: 'in' as 'in' | 'out', qty: '', rate: null, date: toISODate(new Date()), note: '',
  });

  useEffect(() => {
    if (form.itemId && !item) itemService.get(form.itemId).then(setItem).catch(() => {});
  }, [form.itemId, item]);

  const direction = form.reason === 'damaged' ? 'out' : form.direction;
  const qty = Number(form.qty) || 0;
  const after = item ? (item.currentStock ?? 0) + (direction === 'in' ? qty : -qty) : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: FormErrors = {};
    if (!form.itemId) errs.itemId = 'Pick a material';
    if (!(qty > 0)) errs.qty = 'Enter a quantity more than 0';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      await stockService.adjust({
        itemId: form.itemId as string, direction, qty, reason: form.reason, date: form.date || undefined,
        ...(form.rate != null ? { rate: form.rate } : {}), note: trimOrUndef(form.note),
      });
      toast.success('Stock updated');
      navigate(`/materials/${form.itemId}`, { replace: true });
    } catch (err) {
      setErrors(serverFieldErrors(err));
      toast.error(err.message);
      setSaving(false);
    }
  };

  const actions = (
    <>
      <Button variant="outline" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
      <Button type="submit" form="adjust-form" leftIcon="check" loading={saving}>Save adjustment</Button>
    </>
  );

  return (
    <>
      <PageHeader title="Adjust stock" subtitle="For purchases use Purchases; for jobs issue material from the job." actions={actions} />
      <form id="adjust-form" onSubmit={submit} noValidate>
        <TwoColumn
          main={
            <>
              <FormSection title="Adjustment">
                <ItemPicker
                  type="material"
                  label="Material"
                  required
                  value={form.itemId}
                  selectedLabel={item?.name}
                  onChange={(id, it) => {
                    setForm((f) => ({ ...f, itemId: id }));
                    setItem(it);
                  }}
                  error={errors.itemId}
                  containerClassName="sm:col-span-2"
                />
                <Select label="Reason" options={REASONS} value={form.reason} onChange={set('reason')} searchable={false} />
                <Select label="Direction" options={DIRECTIONS} value={direction} onChange={set('direction')} disabled={form.reason === 'damaged'} searchable={false} />
                <Input label="Quantity" required inputMode="decimal" value={form.qty} onChange={(e) => set('qty')(e.target.value.replace(/[^\d.]/g, ''))} error={errors.qty} hint={item ? `In ${item.unit}` : undefined} />
                <MoneyInput label="Cost per unit" value={form.rate} onChange={set('rate')} placeholder="Average cost" hint="Leave empty to use the average cost" />
                <DatePicker label="Date" value={form.date} onChange={set('date')} max={toISODate(new Date())} />
                <Textarea label="Note" rows={2} value={form.note} onChange={set('note')} maxLength={500} containerClassName="sm:col-span-2" placeholder="e.g. Counted on 30 Sept" />
              </FormSection>
              <div className="flex justify-end gap-2">{actions}</div>
            </>
          }
          aside={
            <Card>
              <CardHeader title="Stock after saving" />
              <div className="p-5 pt-4">
                {item ? (
                  <KeyValues
                    rows={[
                      ['Now', `${item.currentStock ?? 0} ${item.unit}`],
                      [direction === 'in' ? 'Adding' : 'Removing', `${qty} ${item.unit}`],
                      ['After', `${after} ${item.unit}`, { strong: true, tone: (after ?? 0) < 0 ? 'text-red-600' : undefined }],
                      ['Average cost', money(item.avgPurchaseRate)],
                    ]}
                  />
                ) : (
                  <p className="text-sm text-ink-400">Pick a material to see its stock.</p>
                )}
              </div>
            </Card>
          }
        />
      </form>
    </>
  );
}
